import React from 'react';
import SEO from '../../../component/common/SEO';
import { pageSEO } from '../../../config/seoConfig';
import WebButton from '../../../component/library/webButton';

import BannerImage from '../../assets/images/be-a-supplier/banner_image.png';
import ImageOne from '../../assets/images/be-a-supplier/how_to_sell_one.png';
import ImageTwo from '../../assets/images/be-a-supplier/how_to_sell_two.png';
import ImageThree from '../../assets/images/be-a-supplier/how_to_sell_three.png';
import ImageFour from '../../assets/images/be-a-supplier/how_to_sell_four.png';

import GrowOne from '../../assets/images/be-a-supplier/grow_one.png';
import GrowTwo from '../../assets/images/be-a-supplier/grow_two.png';
import GrowThree from '../../assets/images/be-a-supplier/grow_three.png';
import GrowFour from '../../assets/images/be-a-supplier/grow_four.png';

import GmailIcon from '../../../assets/images/icons/gmail.png';
import ContactIcon from '../../../assets/images/icons/contact.png';

import { Link, useNavigate } from 'react-router-dom';
import { importerrContact } from '../../helper/constants';
import './style.css';

export default function BeASeller() {
  const howItWorks = [
    { img: ImageOne, title: 'Discover Products', text: 'Browse trending global products curated by Importerr’s AI system.' },
    { img: ImageTwo, title: 'List & Sell', text: 'Add products to your online store or marketplace.' },
    { img: ImageThree, title: 'We Handle Logistics', text: 'Importerr takes care of sourcing, shipping, customs, and delivery.' },
    {
      img: ImageFour,
      title: 'Grow Your Business',
      text: 'Focus on marketing and sales while we ensure timely, reliable delivery to your customers.',
    },
  ];

  const whyChoose = [
    {
      img: GrowOne,
      title: 'Start Without Inventory or Investment',
      text: 'Forget warehouses and bulk orders. With Importerr’s asset-light model, you can sell trending products directly to buyers without upfront investment or storage costs.',
    },
    {
      img: GrowTwo,
      title: 'Access a Curated Global Catalog',
      text: 'Discover and sell products sourced from trusted international suppliers — from lifestyle and electronics to home décor and fashion. Our AI-driven platform helps you identify trending items that buyers want.',
    },
    {
      img: GrowThree,
      title: 'Simplified Selling with End-to-End Support',
      text: 'Importerr handles the hard part — sourcing, freight, customs clearance, and delivery within India. You focus on selling, while our tech-driven system ensures smooth and reliable transactions.',
    },
    {
      img: GrowFour,
      title: 'Perfect for Everyone',
      text: 'Whether you’re a student looking to earn, a homemaker building a side hustle, a startup launching a D2C brand, or an aspiring entrepreneur — Importerr lets anyone create and scale their business.',
    },
    {
      img: GrowOne,
      title: 'Build Your Brand Effortlessly',
      text: 'Create your own store or online presence, showcase global products under your brand, and gain credibility. With Importerr, you can scale faster, attract customers, and grow sustainably.',
    },
    {
      img: GrowTwo,
      title: 'Minimal Risk, Maximum Growth',
      text: 'No hidden costs. No middlemen. No inventory risk. Just a simple, tech-driven platform that allows you to test, sell, and expand your business with confidence.',
    },
  ];

  const navigate = useNavigate();

  return (
    <div className="be_a_supplier_bg">
      <SEO {...pageSEO.beASeller} />
      {/* Banner */}
      <div className="banner">
        <div className="column_one">
          <h1>
            Be a Seller
            <span> — Importerr</span>
          </h1>
          <p className="banner_sub">Sell Global Products. Build Your Business. No Inventory Needed.</p>
          <div className="banner_btns">
            <WebButton className="active_btn_with_shadow" type="button" onClick={() => navigate('/login')} text="Join Importerr" />
            <WebButton className="inactive_btn_with_shadow" type="button" onClick={() => navigate('/login')} text="Log In" />
          </div>
        </div>
        <div className="column_two">
          <img src={BannerImage} alt="Be a Seller Banner" />
        </div>
      </div>

      {/* Intro Copy (as-is text) */}
      <div className="intro_copy_card">
        <p>
          The world is full of trending products — and millions of buyers in India are eager to purchase them. Importerr empowers you to start your
          own business by selling international products, without the hassle of inventory, bulk purchases, or complicated logistics. Whether you’re a
          student, homemaker, startup founder, or D2C brand, Importerr makes it simple to access global products and sell them online.
        </p>
      </div>

      {/* Why Choose Importerr (Tailwind-only, no bottom extra space) */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center">Why Choose Importerr to Sell Global Products?</h2>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {whyChoose.map((item, i) => (
            <article
              key={i}
              className="group rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow flex flex-col overflow-hidden"
            >
              {/* Image area with padding; image scales down inside; no card growth */}
              <div className="flex items-center justify-center p-4 min-h-[140px] bg-slate-50">
                <img
                  src={item.img}
                  alt={item.title}
                  className="max-h-36 max-w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                />
              </div>

              {/* Text area — no flex-grow so no extra bottom space */}
              <div className="p-5">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-[15px] leading-7 text-slate-700">{item.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <div className="how_to_sell_container">
        <div className="how_to_sell_head">How It Works</div>

        <div className="how_to_sell_head_row">
          {howItWorks.map((step, index) => (
            <div className="how_to_sell_col" key={index}>
              <img src={step.img} alt={step.title} />
              <div className="how_to_sell_col_content">
                <p className="how_step_title">{step.title}</p>
                <p className="how_step_text">{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA — Start Your Journey */}
      <div className="journey_cta">
        <h2>Start Your Journey Today</h2>
        <p>
          The future of entrepreneurship is asset-light and global. Turn your ideas into income, build a brand, and access international products with
          ease. Join Importerr today and become a seller of global products!
        </p>
        <div className="cta_btns">
          <WebButton className="active_btn_with_shadow" type="button" onClick={() => navigate('/login')} text="Get Started" />
          <WebButton className="inactive_btn_with_shadow" type="button" onClick={() => navigate('/login')} text="Talk to Support" />
        </div>
      </div>

      {/* <Support /> */}
    </div>
  );
}

const Support = () => {
  return (
    <div className="support_contact_section">
      <h1>24/7 Supplier Support Available at Importerr</h1>
      <p>
        Our dedicated supplier support team is here to address all your inquiries and concerns, providing assistance both before and after you launch
        your online selling business.
      </p>
      <div className="support_contact_row">
        <Link className="suport_contact_column" to={`mailto:${importerrContact.email}`}>
          <img src={GmailIcon} alt="Gmail" />
          <p>{importerrContact.email}</p>
        </Link>

        <Link className="suport_contact_column" to={`tel:${importerrContact.phone}`}>
          <img src={ContactIcon} alt="Phone" />
          <p>{importerrContact.phone}</p>
        </Link>
      </div>
    </div>
  );
};
